<template>
  <div class="inline-block">
    <input
      :id="id"
      type="radio"
      :checked="modelValue === value"
      :disabled="disabled"
      :name="name"
      :value="value"
      tabindex="0"
      :required="required"
      :aria-labelledby="`${id}-label`"
      :aria-invalid="!!error"
      :aria-describedby="
        error ? `${id}-error` : helpLabel ? `${id}-help` : undefined
      "
      class="peer sr-only"
      @click="updateValue"
      @keydown.space.prevent="updateValue"
      @keydown.enter.prevent="updateValue"
    />
    <label
      :id="`${id}-label`"
      :for="id"
      :class="[
        'inline-flex items-center',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      ]"
    >
      <span
        :class="[
          'radio-container w-5 h-5 relative inline-block border border-0.5 rounded-full transition-colors duration-200 ease-in-out',
          modelValue == value && !disabled && 'bg-red-1 border-transparent',
          modelValue != value && !disabled ? 'bg-white dark:bg-dark-surface border-grey-11 dark:border-dark-border' : '',
          modelValue != value && disabled ? 'bg-white dark:bg-dark-surface border-grey-12 dark:border-dark-border' : '',
          modelValue == value && disabled ? 'bg-grey-8 dark:bg-dark-border border-transparent' : '',
          !disabled && 'hover:border-red-1',
        ]"
        role="radio"
        :aria-checked="modelValue === value"
        :aria-disabled="disabled"
        :aria-labelledby="`${id}-label`"
      >
        <span class="absolute inset-0 flex items-center justify-center">
          <span
            v-if="modelValue == value"
            :class="[
              'block w-2.5 h-2.5 relative rounded-full',
              disabled ? 'bg-white' : 'bg-white',
            ]"
            role="presentation"
            aria-hidden="true"
          ></span>
        </span>
      </span>
      <span
        class="ml-3 text-base font-medium dark:text-dark-ink"
        :class="[disabled ? 'text-grey-11 dark:text-dark-ink-muted' : '']"
      >
        {{ label }} <AppRequiredLabel v-if="required" />
      </span>
    </label>
    <AppErrorLabel v-if="error" :id="`${id}-error`" class="mt-1"
      >{{ error }}
    </AppErrorLabel>
    <AppHelpLabel v-if="helpLabel" :id="`${id}-help`" class="mt-1"
      >{{ helpLabel }}
    </AppHelpLabel>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    label: string
    modelValue?: string | number | boolean
    value?: string | number | boolean
    allowUnselect?: boolean
    disabled?: boolean
    required?: boolean
    helpLabel?: string
    error?: string
    name?: string
    id?: string
  }>(),
  {
    modelValue: '',
    value: '',
    allowUnselect: true,
    disabled: false,
    required: false,
    helpLabel: '',
    error: '',
    name: () => `radio-${Math.random().toString(36).substr(2, 9)}`,
    id: () => `radio-${Math.random().toString(36).substr(2, 9)}`,
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean | null]
}>()

const updateValue = () => {
  if (props.value == props.modelValue && props.allowUnselect) {
    emit('update:modelValue', null)
  } else {
    emit('update:modelValue', props.value)
  }
}
</script>

<style>
@reference "../../../../css/app.css";
.radio-container:not(.disabled):hover::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 50%;
}

.dark .radio-container:not(.disabled):hover::before {
  background-color: rgba(255, 255, 255, 0.08);
}

input:focus-visible + label .radio-container {
  @apply ring-2 ring-red-1 ring-offset-2;
}
</style>
